import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import {
  TrendingUp,
  Zap,
  ShieldCheck,
  Compass,
  Users,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  FileText,
  Calculator,
  Send,
  Car,
  Calendar,
  Building2,
  Star,
  BarChart3,
  PieChart as PieChartIcon,
  Layers,
  Phone,
  Mail,
  Clock,
  Award,
} from "lucide-react";
import "./Landing.css";
import Header from "../components/Header";
import FAQ from "../components/FAQ";
import AppDownloadCard from "../app-platform/AppDownloadCard";

const Landing = () => {
  const navigate = useNavigate();

  // Active tab for interactive CRM Product Showcase
  const [activeTab, setActiveTab] = useState("analytics");

  // State for ROI Calculator
  const [leadsCount, setLeadsCount] = useState(120);
  const [avgPackageVal, setAvgPackageVal] = useState(65000);
  const [conversionRate, setConversionRate] = useState(15);

  // State for Demo Form
  const [demoForm, setDemoForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    teamSize: "1-5",
    goal: "all",
  });
  const [demoSubmitted, setDemoSubmitted] = useState(false);

  // Sample data for charts
  const revenueData = [
    { month: "Jan", revenue: 42, bookings: 120, leads: 340 },
    { month: "Feb", revenue: 58, bookings: 165, leads: 420 },
    { month: "Mar", revenue: 75, bookings: 210, leads: 510 },
    { month: "Apr", revenue: 62, bookings: 180, leads: 460 },
    { month: "May", revenue: 89, bookings: 250, leads: 620 },
    { month: "Jun", revenue: 105, bookings: 290, leads: 740 },
    { month: "Jul", revenue: 118, bookings: 330, leads: 810 },
    { month: "Aug", revenue: 142, bookings: 390, leads: 950 },
  ];

  const leadSourcesData = [
    { name: "Meta Ads", value: 42, color: "#4F46E5" },
    { name: "Website Direct", value: 28, color: "#10B981" },
    { name: "WhatsApp B2B", value: 18, color: "#F59E0B" },
    { name: "Google Organic", value: 12, color: "#EC4899" },
  ];

  const quotationData = [
    { type: "Draft", count: 45, value: "₹29.2L" },
    { type: "Sent", count: 128, value: "₹83.2L" },
    { type: "Accepted", count: 86, value: "₹55.9L" },
    { type: "Converted", count: 72, value: "₹46.8L" },
  ];

  const features = [
    {
      icon: <Compass className="feature-icon-svg" />,
      title: "Customized CRM Solutions",
      badge: "Travel Purpose-Built",
      description:
        "Designed exclusively for travel agencies, tour operators, and DMCs with travel-centric lead stages, passenger details, and itinerary pipelines.",
    },
    {
      icon: <Zap className="feature-icon-svg" />,
      title: "Automated Lead Distribution",
      badge: "Instant Routing",
      description:
        "Capture leads directly from Meta Ads, Google Ads, WhatsApp, and website forms. Auto-assign inquiries to sales executives instantly.",
    },
    {
      icon: <Calculator className="feature-icon-svg" />,
      title: "Smart Quotation & Margin Engine",
      badge: "Dynamic Pricing",
      description:
        "Auto-calculate seasonal hotel rates, transport costs, and enforce company minimum margin percentages before quote generation.",
    },
    {
      icon: <Car className="feature-icon-svg" />,
      title: "Transport Operations Hub",
      badge: "Driver Dispatch",
      description:
        "Manage vehicle allocations, assign drivers & suppliers per day, generate 1-click WhatsApp briefs, and export clean operational PDFs.",
    },
    {
      icon: <Sparkles className="feature-icon-svg" />,
      title: "AI Itinerary Builder",
      badge: "Fast Generation",
      description:
        "Generate day-wise travel itineraries with auto-filled hotels, sightseeing spots, hot offers, and polished client presentation documents.",
    },
    {
      icon: <Building2 className="feature-icon-svg" />,
      title: "Unified Accounts & Supplier Payments",
      badge: "Financial Control",
      description:
        "Track customer installments, supplier vouchers, hotel payouts, GST invoices, and financial health reports seamlessly.",
    },
    {
      icon: <ShieldCheck className="feature-icon-svg" />,
      title: "Advanced Data Protection",
      badge: "Bank-Grade Security",
      description:
        "Encrypted data vaults, granular role-based permissions (Admin, Sales, Ops, Accounts), and automatic daily cloud backups.",
    },
    {
      icon: <TrendingUp className="feature-icon-svg" />,
      title: "Real-Time Growth Analytics",
      badge: "Live Dashboards",
      description:
        "Executive BI dashboards, lead conversion funnels, agent performance leaderboards, and revenue growth forecasting.",
    },
  ];

  const clients = [
    { name: "Destino Trips", tag: "DMC & Luxury Tours", rating: "5.0 ★" },
    { name: "Life Travello", tag: "Outbound Expeditions", rating: "4.9 ★" },
    { name: "Holiday Hackerz", tag: "Domestic Packages", rating: "5.0 ★" },
    { name: "Hilltop Hikers", tag: "Adventure & Treks", rating: "4.8 ★" },
    { name: "Mountain Bae", tag: "Himalayan Specialists", rating: "4.9 ★" },
    { name: "Nestro Trips", tag: "Customized Travel", rating: "5.0 ★" },
  ];

  const testimonials = [
    {
      quote:
        "TRAVNOK transformed our operations. Our quotation turnaround time dropped from 4 hours to just 10 minutes, and our lead conversion rate shot up by 38% in 60 days!",
      author: "Rahul Sharma",
      role: "Managing Director",
      company: "Destino Trips",
      avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=150",
    },
    {
      quote:
        "The Transport Operations & Driver Brief module alone saved us endless WhatsApp chaos during peak season. Every driver gets clear daily duties with 1 click.",
      author: "Priya Malhotra",
      role: "Head of Operations",
      company: "Life Travello",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=150",
    },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // ROI Calculations
  const calculatedCurrentRevenue = Math.round((leadsCount * (conversionRate / 100)) * avgPackageVal);
  const calculatedBoostedRevenue = Math.round((leadsCount * ((conversionRate * 1.35) / 100)) * avgPackageVal);
  const calculatedExtraRevenue = calculatedBoostedRevenue - calculatedCurrentRevenue;
  const hoursSaved = Math.round(leadsCount * 0.4);

  const handleDemoSubmit = (e) => {
    e.preventDefault();
    if (!demoForm.name || !demoForm.email || !demoForm.phone) {
      alert("Please fill in your name, email, and mobile number.");
      return;
    }
    setDemoSubmitted(true);
  };

  return (
    <div className="landing-page" id="hero">
      <Header scrollToSection={scrollToSection} />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg-glow"></div>
        <div className="hero-grid-pattern"></div>
        
        <div className="container hero-container">
          <div className="hero-badge-container">
            <span className="hero-pill-badge">
              <Sparkles size={14} className="badge-sparkle" />
              Next-Gen AI Travel CRM Platform for DMCs & Agencies
            </span>
          </div>

          <h1 className="hero-title">
            Transform Your Travel Business with{" "}
            <span className="text-gradient">Enterprise Intelligence</span>
          </h1>

          <p className="hero-subtitle">
            Automate lead capture, generate multi-option quotations with margin enforcement,
            build day-wise AI itineraries, and dispatch transport briefs — all in one unified platform.
          </p>

          <div className="hero-buttons">
            <button
              className="btn btn-primary"
              onClick={() => scrollToSection("demo-form")}
            >
              Book a Live Demo <ArrowRight size={18} />
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => scrollToSection("interactive-showcase")}
            >
              Explore Interactive CRM <BarChart3 size={18} />
            </button>
          </div>

          <AppDownloadCard variant="dark" />

          {/* Stats Bar */}
          <div className="hero-stats-bar">
            <div className="stat-item">
              <div className="stat-number">₹450Cr+</div>
              <div className="stat-label">Bookings Managed</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">99.8%</div>
              <div className="stat-label">Quotation Speedup</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">3.5x</div>
              <div className="stat-label">Higher Lead Conversion</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">500+</div>
              <div className="stat-label">Travel Agencies & DMCs</div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Product Showcase Section */}
      <section id="interactive-showcase" className="showcase-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">LIVE PLATFORM DEMO</span>
            <h2 className="section-title">
              Built Specifically for the Way Modern Travel Companies Work
            </h2>
            <p className="section-subtitle">
              Explore how TRAVNOK unifies lead pipeline, quotation calculations, transport operations, and real-time revenue analytics.
            </p>
          </div>

          {/* Tab Switcher */}
          <div className="showcase-tabs">
            <button
              className={`tab-btn ${activeTab === "analytics" ? "active" : ""}`}
              onClick={() => setActiveTab("analytics")}
            >
              <BarChart3 size={18} /> Revenue Analytics
            </button>
            <button
              className={`tab-btn ${activeTab === "quotations" ? "active" : ""}`}
              onClick={() => setActiveTab("quotations")}
            >
              <Calculator size={18} /> Smart Quotation Engine
            </button>
            <button
              className={`tab-btn ${activeTab === "operations" ? "active" : ""}`}
              onClick={() => setActiveTab("operations")}
            >
              <Car size={18} /> Transport & Operations
            </button>
            <button
              className={`tab-btn ${activeTab === "itinerary" ? "active" : ""}`}
              onClick={() => setActiveTab("itinerary")}
            >
              <Calendar size={18} /> AI Itinerary Builder
            </button>
          </div>

          {/* Tab Content Display */}
          <div className="showcase-card-container">
            {activeTab === "analytics" && (
              <div className="showcase-grid animated-fade-in">
                <div className="showcase-main-chart">
                  <div className="chart-header">
                    <div>
                      <h3 className="chart-title">Revenue & Booking Growth (YTD)</h3>
                      <p className="chart-sub">Real-time revenue performance and booking frequency tracking</p>
                    </div>
                    <span className="chart-badge">+28.4% YoY</span>
                  </div>
                  <div style={{ width: "100%", height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={revenueData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="month" stroke="#94A3B8" />
                        <YAxis stroke="#94A3B8" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#1E293B",
                            borderColor: "#334155",
                            borderRadius: "8px",
                            color: "#FFF",
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="revenue"
                          name="Revenue (₹ Lakhs)"
                          stroke="#4F46E5"
                          strokeWidth={3}
                          fillOpacity={1}
                          fill="url(#revenueGrad)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="showcase-side-panel">
                  <div className="side-card">
                    <h4>Inbound Lead Breakdown</h4>
                    <div style={{ width: "100%", height: 180 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={leadSourcesData}
                            cx="50%"
                            cy="50%"
                            innerRadius={45}
                            outerRadius={70}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {leadSourcesData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="pie-legend">
                      {leadSourcesData.map((item, idx) => (
                        <div key={idx} className="legend-item">
                          <span className="legend-dot" style={{ backgroundColor: item.color }}></span>
                          <span>{item.name}: {item.value}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "quotations" && (
              <div className="showcase-grid animated-fade-in">
                <div className="showcase-main-chart">
                  <div className="chart-header">
                    <div>
                      <h3 className="chart-title">Pipeline Quotation Conversion</h3>
                      <p className="chart-sub">Automated pricing calculations with enforced minimum margin safeguards</p>
                    </div>
                    <span className="chart-badge green">Avg Margin: 18.5%</span>
                  </div>
                  <div style={{ width: "100%", height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={quotationData} margin={{ top: 20, right: 20, left: -10, bottom: 0 }}>
                        <XAxis dataKey="type" stroke="#94A3B8" />
                        <YAxis stroke="#94A3B8" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#1E293B",
                            borderColor: "#334155",
                            borderRadius: "8px",
                            color: "#FFF",
                          }}
                        />
                        <Bar dataKey="count" name="Quotations Count" fill="#10B981" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="showcase-side-panel">
                  <div className="quote-preview-card">
                    <div className="quote-card-header">
                      <span>QUOTATION #QT-2026-882</span>
                      <span className="status-tag confirmed">Confirmed</span>
                    </div>
                    <h5>5 Days Himachal Scenic Delight</h5>
                    <div className="quote-breakdown">
                      <div className="row"><span>2x Deluxe Hotel Rooms</span><strong>₹28,500</strong></div>
                      <div className="row"><span>Private Innova Crysta (5 Days)</span><strong>₹18,000</strong></div>
                      <div className="row"><span>Sightseeing & Activities</span><strong>₹6,200</strong></div>
                      <div className="row margin-row"><span>Agency Margin (18%)</span><strong className="text-emerald">₹9,486</strong></div>
                      <div className="row total-row"><span>Total Quotation</span><strong className="text-primary">₹62,186</strong></div>
                    </div>
                    <button className="btn btn-sm-primary w-full mt-3">
                      Export Clean PDF <FileText size={14} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "operations" && (
              <div className="showcase-operations-view animated-fade-in">
                <div className="ops-header">
                  <div>
                    <h3 className="chart-title">Live Transport Operations & Vehicle Dispatch</h3>
                    <p className="chart-sub">Assign drivers, specify vehicle numbers, and dispatch instant briefs via WhatsApp or PDF.</p>
                  </div>
                  <span className="ops-status-badge">All 14 Vehicles Assigned</span>
                </div>

                <div className="ops-cards-row">
                  <div className="ops-trip-card">
                    <div className="ops-card-top">
                      <span className="day-badge">Day 1 - Pick-up</span>
                      <span className="car-tag">Innova Crysta [HP-01-AB-8821]</span>
                    </div>
                    <h4>Chandigarh Airport ➔ Shimla Hotel</h4>
                    <p><Clock size={14} /> Reporting Time: 08:30 AM | Pickup: 09:00 AM</p>
                    <div className="driver-info-box">
                      <Users size={16} />
                      <div>
                        <strong>Driver: Rajesh Kumar</strong>
                        <div className="text-sub">Ph: +91 98765-43210 | Vendor: Himalayan Cabs</div>
                      </div>
                    </div>
                    <button className="btn-brief-action">
                      <Send size={14} /> Share WhatsApp Brief
                    </button>
                  </div>

                  <div className="ops-trip-card">
                    <div className="ops-card-top">
                      <span className="day-badge">Day 2 - Sightseeing</span>
                      <span className="car-tag">Tempo Traveller [HP-02-C-4412]</span>
                    </div>
                    <h4>Shimla Local & Kufri Excursion</h4>
                    <p><Clock size={14} /> Reporting Time: 09:00 AM | Pickup: 09:30 AM</p>
                    <div className="driver-info-box">
                      <Users size={16} />
                      <div>
                        <strong>Driver: Vikram Singh</strong>
                        <div className="text-sub">Ph: +91 98123-99881 | Vendor: Self Owned</div>
                      </div>
                    </div>
                    <button className="btn-brief-action">
                      <Send size={14} /> Share WhatsApp Brief
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "itinerary" && (
              <div className="showcase-itinerary-view animated-fade-in">
                <div className="itinerary-header">
                  <div>
                    <h3 className="chart-title">AI-Powered Multi-Day Itinerary Builder</h3>
                    <p className="chart-sub">Auto-resolves hotel seasons, selects hot offer inclusions, and builds day-wise timelines.</p>
                  </div>
                  <button className="btn btn-sm-primary">Auto-Generate New Itinerary <Sparkles size={14} /></button>
                </div>

                <div className="itinerary-timeline">
                  <div className="timeline-day">
                    <div className="day-number">Day 01</div>
                    <div className="day-content">
                      <h4>Arrival in Srinagar & Shikara Ride on Dal Lake</h4>
                      <p>Warm welcome at Srinagar Airport. Private transfer to Luxury Houseboat. Evening sunset Shikara ride included.</p>
                      <div className="day-meta">
                        <span className="meta-pill"><Building2 size={12} /> Hotel: Grand Palace Srinagar (Deluxe Lake View)</span>
                        <span className="meta-pill"><Car size={12} /> Vehicle: Sedan Transfer</span>
                      </div>
                    </div>
                  </div>

                  <div className="timeline-day">
                    <div className="day-number">Day 02</div>
                    <div className="day-content">
                      <h4>Srinagar to Gulmarg Excursion & Gondola Ride</h4>
                      <p>Morning scenic drive through pine forests. Excursion to Gulmarg. Phase 1 Gondola tickets pre-booked.</p>
                      <div className="day-meta">
                        <span className="meta-pill"><Building2 size={12} /> Hotel: Khyber Resort Gulmarg</span>
                        <span className="meta-pill"><Sparkles size={12} /> Inclusions: Gondola Tickets & Breakfast</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section id="features" className="features-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">COMPREHENSIVE FEATURES</span>
            <h2 className="section-title">Everything Your Travel Business Needs to Scale</h2>
            <p className="section-subtitle">
              From lead capture to final booking vouchers, TRAVNOK gives your sales, operations, and accounting teams complete clarity.
            </p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-card-header">
                  <div className="feature-icon-wrapper">{feature.icon}</div>
                  <span className="feature-card-badge">{feature.badge}</span>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <div className="feature-learn-more">
                  Learn more <ArrowRight size={14} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive ROI Calculator Section */}
      <section id="roi-calculator" className="roi-section">
        <div className="container">
          <div className="roi-wrapper">
            <div className="roi-info">
              <span className="section-tag">GROWTH ESTIMATOR</span>
              <h2>Calculate Your Business Growth Potential with TRAVNOK</h2>
              <p>
                See how much extra revenue and operational time your agency can unlock every month with automated quotations and instant lead routing.
              </p>

              <div className="roi-value-cards">
                <div className="roi-val-card highlight">
                  <span className="roi-label">Projected Monthly Revenue Boost</span>
                  <div className="roi-big-stat">+₹{calculatedExtraRevenue.toLocaleString('en-IN')}</div>
                  <span className="roi-subtext">Based on a 35% improvement in quotation response speed & follow-up efficiency</span>
                </div>

                <div className="roi-val-card">
                  <span className="roi-label">Hours Saved Every Month</span>
                  <div className="roi-big-stat">{hoursSaved * 4} hrs</div>
                  <span className="roi-subtext">Eliminating manual hotel calculations, driver brief typing, & manual vouchers</span>
                </div>
              </div>
            </div>

            <div className="roi-controls-card">
              <h3>Custom Agency Estimator</h3>
              <p className="roi-hint">Adjust the sliders below based on your current volume:</p>

              <div className="slider-group">
                <div className="slider-label">
                  <span>Monthly Inbound Leads:</span>
                  <strong>{leadsCount} Leads/mo</strong>
                </div>
                <input
                  type="range"
                  min="20"
                  max="1000"
                  step="10"
                  value={leadsCount}
                  onChange={(e) => setLeadsCount(Number(e.target.value))}
                />
              </div>

              <div className="slider-group">
                <div className="slider-label">
                  <span>Average Package Price (₹):</span>
                  <strong>₹{avgPackageVal.toLocaleString('en-IN')}</strong>
                </div>
                <input
                  type="range"
                  min="10000"
                  max="300000"
                  step="5000"
                  value={avgPackageVal}
                  onChange={(e) => setAvgPackageVal(Number(e.target.value))}
                />
              </div>

              <div className="slider-group">
                <div className="slider-label">
                  <span>Current Conversion Rate (%):</span>
                  <strong>{conversionRate}%</strong>
                </div>
                <input
                  type="range"
                  min="5"
                  max="40"
                  step="1"
                  value={conversionRate}
                  onChange={(e) => setConversionRate(Number(e.target.value))}
                />
              </div>

              <div className="roi-calc-summary mt-4">
                <div className="summary-row">
                  <span>Current Monthly Revenue:</span>
                  <strong>₹{calculatedCurrentRevenue.toLocaleString('en-IN')}</strong>
                </div>
                <div className="summary-row boosted">
                  <span>Boosted Revenue with TRAVNOK:</span>
                  <strong className="text-emerald">₹{calculatedBoostedRevenue.toLocaleString('en-IN')}</strong>
                </div>
              </div>

              <button
                className="btn btn-primary w-full mt-4"
                onClick={() => scrollToSection("demo-form")}
              >
                Claim Your Growth Strategy Demo <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Clients & Testimonials Section */}
      <section className="clients-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">TRUSTED PARTNERS</span>
            <h2 className="section-title">Empowering Top Travel Agencies & DMCs</h2>
            <p className="section-subtitle">
              Leading tour operators rely on TRAVNOK daily to run sales pipelines, manage suppliers, and deliver unforgettable journeys.
            </p>
          </div>

          <div className="clients-grid">
            {clients.map((client, index) => (
              <div key={index} className="client-logo-card">
                <div className="client-name">{client.name}</div>
                <div className="client-tag">{client.tag}</div>
                <div className="client-rating">{client.rating}</div>
              </div>
            ))}
          </div>

          <div className="testimonials-grid mt-5">
            {testimonials.map((t, idx) => (
              <div key={idx} className="testimonial-card">
                <div className="stars">★★★★★</div>
                <p className="quote-text">"{t.quote}"</p>
                <div className="author-info">
                  <img src={t.avatar} alt={t.author} className="author-img" />
                  <div>
                    <strong>{t.author}</strong>
                    <div className="author-role">{t.role}, {t.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">QUESTIONS & ANSWERS</span>
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">Got questions about setup, data security, or workflow customization? We've got answers.</p>
          </div>
          <FAQ />
        </div>
      </section>

      {/* Book Demo Section */}
      <section id="demo-form" className="demo-section">
        <div className="container">
          <div className="demo-container-card">
            <div className="demo-info-side">
              <span className="demo-badge">GET STARTED IN MINUTES</span>
              <h2>Ready to Scale Your Travel Agency Revenue?</h2>
              <p>
                Book a personalized, 1-on-1 walkthrough with a travel CRM expert. We'll show you how TRAVNOK configures to your exact destination products and operational workflow.
              </p>

              <div className="demo-perks">
                <div className="perk-item">
                  <CheckCircle2 className="perk-icon" />
                  <span>Free 14-Day Full-Access Trial</span>
                </div>
                <div className="perk-item">
                  <CheckCircle2 className="perk-icon" />
                  <span>Custom Import of Existing Leads & Data</span>
                </div>
                <div className="perk-item">
                  <CheckCircle2 className="perk-icon" />
                  <span>Dedicated Onboarding & Operations Support</span>
                </div>
              </div>
            </div>

            <div className="demo-form-side">
              {demoSubmitted ? (
                <div className="demo-success-box">
                  <Award size={48} className="text-emerald mb-2" />
                  <h3>Demo Requested Successfully!</h3>
                  <p>
                    Thank you, <strong>{demoForm.name}</strong>! One of our travel CRM specialists will contact you at <strong>{demoForm.phone}</strong> shortly to schedule your live walkthrough.
                  </p>
                  <button className="btn btn-secondary mt-3" onClick={() => setDemoSubmitted(false)}>
                    Submit Another Request
                  </button>
                </div>
              ) : (
                <form className="landing-demo-form" onSubmit={handleDemoSubmit}>
                  <h3>Schedule Your Live Demo</h3>

                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      placeholder="e.g. Ankit Verma"
                      required
                      value={demoForm.name}
                      onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Business Email *</label>
                      <input
                        type="email"
                        placeholder="ankit@travelagency.com"
                        required
                        value={demoForm.email}
                        onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Mobile / WhatsApp *</label>
                      <input
                        type="tel"
                        placeholder="+91 98765-43210"
                        required
                        value={demoForm.phone}
                        onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Company / Agency Name</label>
                      <input
                        type="text"
                        placeholder="e.g. Royal Himalayan DMC"
                        value={demoForm.company}
                        onChange={(e) => setDemoForm({ ...demoForm, company: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Team Size</label>
                      <select
                        value={demoForm.teamSize}
                        onChange={(e) => setDemoForm({ ...demoForm, teamSize: e.target.value })}
                      >
                        <option value="1-5">1 - 5 Users</option>
                        <option value="6-20">6 - 20 Users</option>
                        <option value="21-50">21 - 50 Users</option>
                        <option value="50+">50+ Users (Enterprise)</option>
                      </select>
                    </div>
                  </div>

                  <button type="submit" className="btn btn-primary w-full mt-2">
                    Request Personal Demo <ArrowRight size={18} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="logo footer-logo">
                <span className="logo-badge">TRAVNOK</span>
                <span className="logo-sub">CRM</span>
              </div>
              <p className="footer-desc">
                Empowering travel agencies, tour operators, and DMCs worldwide with enterprise-grade CRM intelligence, automated quotations, and transport dispatching.
              </p>
            </div>

            <div className="footer-column">
              <h4>Platform</h4>
              <ul>
                <li><a href="#interactive-showcase" onClick={(e) => { e.preventDefault(); scrollToSection("interactive-showcase"); }}>Revenue Analytics</a></li>
                <li><a href="#interactive-showcase" onClick={(e) => { e.preventDefault(); scrollToSection("interactive-showcase"); }}>Smart Quotation Engine</a></li>
                <li><a href="#interactive-showcase" onClick={(e) => { e.preventDefault(); scrollToSection("interactive-showcase"); }}>Transport & Operations</a></li>
                <li><a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection("features"); }}>AI Itinerary Generator</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Solutions</h4>
              <ul>
                <li><a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection("features"); }}>For Travel Agencies</a></li>
                <li><a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection("features"); }}>For Tour Operators</a></li>
                <li><a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection("features"); }}>For Inbound/Outbound DMCs</a></li>
                <li><a href="#roi-calculator" onClick={(e) => { e.preventDefault(); scrollToSection("roi-calculator"); }}>ROI Growth Calculator</a></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Contact & Support</h4>
              <p><Mail size={14} /> info@nestpedia.info</p>
              <p><Phone size={14} /> +91 98765-43210 / +1 (555) 019-2831</p>
              <p><Building2 size={14} /> New Delhi, India & Global Cloud Infrastructure</p>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} TRAVNOK CRM. All rights reserved.</p>
            <div className="footer-bottom-links">
              <a href="#privacy" onClick={(e) => e.preventDefault()}>Privacy Policy</a>
              <span>•</span>
              <a href="#terms" onClick={(e) => e.preventDefault()}>Terms of Service</a>
              <span>•</span>
              <a href="#security" onClick={(e) => e.preventDefault()}>Security & Compliance</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
